/**
 * Catalogue & métadonnées du module Onboarding / Offboarding.
 *
 * Source unique pour : le catalogue des types d'objets, les libellés/attributs
 * d'inventaire, les métadonnées d'affichage des statuts (label FR + couleur +
 * icône) et le calcul d'avancement. Réutilisé par les pages, les composants et
 * le store — ne pas dupliquer ces maps ailleurs.
 */
import type {
  AssetCategory,
  AssetStatus,
  AssetTypeDef,
  AttributeDef,
  CaseStatus,
  CaseType,
  OnboardingCase,
  ReplacementSource,
  ReplacementStatus,
} from './types'

/* -------------------------------------------------------------------------- */
/* Attributs d'inventaire                                                     */
/* -------------------------------------------------------------------------- */

/** Registre des attributs d'inventaire : clé technique → libellé FR (+ sensible). */
export const ATTRIBUTE_DEFS: Record<string, AttributeDef> = {
  serialNumber: { key: 'serialNumber', label: 'N° de série' },
  model: { key: 'model', label: 'Modèle' },
  previousOwner: { key: 'previousOwner', label: 'Propriétaire précédent' },
  emissionDate: { key: 'emissionDate', label: "Date d'émission" },
  physicalDamage: { key: 'physicalDamage', label: 'Dommages physiques' },
  identNumber: { key: 'identNumber', label: "N° d'identification" },
  imei: { key: 'imei', label: 'IMEI' },
  appleId: { key: 'appleId', label: 'ID Apple', sensitive: true },
  applePassword: { key: 'applePassword', label: 'Mot de passe Apple', sensitive: true },
  deviceCode: { key: 'deviceCode', label: "Code de l'appareil", sensitive: true },
  phoneNumber: { key: 'phoneNumber', label: 'N° de téléphone' },
  operator: { key: 'operator', label: 'Opérateur' },
  plan: { key: 'plan', label: 'Forfait' },
  puk: { key: 'puk', label: 'PUK', sensitive: true },
  oldSystemCode: { key: 'oldSystemCode', label: 'Code ancien système' },
  newSystemCode: { key: 'newSystemCode', label: 'Code nouveau système' },
  zones: { key: 'zones', label: "Zones d'accès" },
  quantity: { key: 'quantity', label: 'Quantité' },
  sdaNumber: { key: 'sdaNumber', label: 'N° SDA' },
  extension: { key: 'extension', label: 'Extension' },
  plate: { key: 'plate', label: "Plaque d'immatriculation" },
  fuelCard: { key: 'fuelCard', label: 'Carte carburant' },
  keysCount: { key: 'keysCount', label: 'Nombre de clés' },
  toolName: { key: 'toolName', label: 'Outil' },
  login: { key: 'login', label: 'Identifiant' },
}

/** Résout le libellé FR d'un attribut (fallback : la clé brute). */
export function attributeLabel(key: string): string {
  return ATTRIBUTE_DEFS[key]?.label ?? key
}

/** Indique si un attribut est sensible (🔒 à masquer par défaut). */
export function isSensitiveAttribute(key: string): boolean {
  return ATTRIBUTE_DEFS[key]?.sensitive === true
}

/* -------------------------------------------------------------------------- */
/* Catalogue des types d'objets                                               */
/* -------------------------------------------------------------------------- */

/** Catalogue des types d'objets dotables (repris de la fiche de dotation APY). */
export const ASSET_TYPES: AssetTypeDef[] = [
  { code: 'PC_LAPTOP', label: 'PC portable', category: 'IT', attributeKeys: ['serialNumber', 'model', 'previousOwner', 'emissionDate', 'physicalDamage'] },
  { code: 'PC_DESKTOP', label: 'PC fixe', category: 'IT', attributeKeys: ['serialNumber', 'model'] },
  { code: 'PC_CHARGER', label: 'Chargeur PC portable', category: 'IT', attributeKeys: ['model'] },
  { code: 'BAG', label: 'Sacoche', category: 'OTHER', attributeKeys: [] },
  { code: 'KEYBOARD', label: 'Clavier', category: 'IT', attributeKeys: ['model'] },
  { code: 'MOUSE', label: 'Souris', category: 'IT', attributeKeys: ['model'] },
  { code: 'DOCK', label: "Station d'accueil", category: 'IT', attributeKeys: ['serialNumber', 'model'] },
  { code: 'SCREEN', label: 'Écran', category: 'IT', attributeKeys: ['serialNumber', 'model'] },
  { code: 'HEADSET', label: 'Casque', category: 'IT', attributeKeys: ['model'] },
  { code: 'MOBILE', label: 'Téléphone portable', category: 'TELECOM', attributeKeys: ['identNumber', 'model', 'imei', 'serialNumber', 'appleId', 'applePassword', 'deviceCode'] },
  { code: 'MOBILE_CHARGER', label: 'Chargeur téléphone', category: 'TELECOM', attributeKeys: [] },
  { code: 'SIM', label: 'Carte SIM', category: 'TELECOM', attributeKeys: ['phoneNumber', 'operator', 'puk'] },
  { code: 'MOBILE_LINE', label: 'Ligne mobile pro', category: 'TELECOM', attributeKeys: ['phoneNumber', 'operator', 'plan'] },
  { code: 'FIXED_LINE', label: 'Ligne fixe / 3CX', category: 'TELECOM', attributeKeys: ['sdaNumber', 'extension'] },
  { code: 'BADGE', label: "Badge d'accès", category: 'ACCESS', attributeKeys: ['serialNumber', 'oldSystemCode', 'newSystemCode', 'emissionDate', 'zones'] },
  { code: 'BUSINESS_CARD', label: 'Carte de visite', category: 'OTHER', attributeKeys: ['quantity'] },
  { code: 'VEHICLE', label: 'Véhicule', category: 'VEHICLE', attributeKeys: ['plate', 'model', 'fuelCard', 'keysCount'] },
  { code: 'TOOL_ACCESS', label: 'Accès outil', category: 'ACCESS', attributeKeys: ['toolName', 'login'] },
]

export const ASSET_TYPE_BY_CODE: Record<string, AssetTypeDef> = Object.fromEntries(
  ASSET_TYPES.map((t) => [t.code, t]),
)

/** Métadonnées d'affichage d'une famille d'objet (label FR). */
export const CATEGORY_META: Record<AssetCategory, { label: string }> = {
  IT: { label: 'Informatique' },
  TELECOM: { label: 'Téléphonie' },
  VEHICLE: { label: 'Véhicule' },
  ACCESS: { label: 'Accès & badges' },
  OTHER: { label: 'Autre' },
}

/* -------------------------------------------------------------------------- */
/* Statuts                                                                    */
/* -------------------------------------------------------------------------- */

/** Tonalité visuelle réutilisable (mappée vers une classe DaisyUI badge). */
export type StatusTone = 'neutral' | 'info' | 'warning' | 'success'

/**
 * Métadonnées d'un statut d'objet : libellé FR, tonalité (couleur), nom d'icône
 * Heroicon outline (résolu côté composant). Couleur + icône + label = jamais la
 * couleur seule (MASTER §1).
 */
export const ASSET_STATUS_META: Record<
  AssetStatus,
  { label: string; tone: StatusTone; badgeClass: string; icon: string }
> = {
  TO_ORDER: { label: 'À commander', tone: 'neutral', badgeClass: 'badge-ghost', icon: 'ClipboardDocumentList' },
  ORDERED: { label: 'Commandé', tone: 'info', badgeClass: 'badge-info', icon: 'ShoppingCart' },
  RECEIVED: { label: 'Reçu', tone: 'info', badgeClass: 'badge-info', icon: 'InboxArrowDown' },
  PREPARED: { label: 'Préparé', tone: 'warning', badgeClass: 'badge-warning', icon: 'WrenchScrewdriver' },
  DELIVERED: { label: 'Remis', tone: 'success', badgeClass: 'badge-success', icon: 'CheckCircle' },
  RETURNED: { label: 'Restitué', tone: 'neutral', badgeClass: 'badge-ghost', icon: 'ArrowUturnLeft' },
  REPLACED: { label: 'Remplacé', tone: 'neutral', badgeClass: 'badge-ghost', icon: 'ArrowPath' },
}

/** Ordre logique des statuts dans le workflow (pour tri / progression). */
export const ASSET_STATUS_ORDER: AssetStatus[] = [
  'TO_ORDER',
  'ORDERED',
  'RECEIVED',
  'PREPARED',
  'DELIVERED',
  'RETURNED',
  'REPLACED',
]

/** Métadonnées d'affichage d'un statut de dossier. */
export const CASE_STATUS_META: Record<CaseStatus, { label: string; badgeClass: string }> = {
  DRAFT: { label: 'Brouillon', badgeClass: 'badge-ghost' },
  IN_PROGRESS: { label: 'En cours', badgeClass: 'badge-info' },
  DONE: { label: 'Terminé', badgeClass: 'badge-success' },
}

/** Libellé FR du type de dossier. */
export const CASE_TYPE_META: Record<CaseType, { label: string; short: string }> = {
  ONBOARDING: { label: 'Onboarding', short: 'Arrivée' },
  OFFBOARDING: { label: 'Offboarding', short: 'Départ' },
}

/** Libellé FR de la source du dossier. */
export const CASE_SOURCE_META: Record<OnboardingCase['source'], { label: string }> = {
  LUCCA: { label: 'Lucca (auto)' },
  MANUAL: { label: 'Saisie manuelle' },
}

/* -------------------------------------------------------------------------- */
/* Avancement                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Statut « cible » qui compte comme terminé selon le sens du dossier :
 * onboarding → objet Remis, offboarding → objet Restitué.
 */
function targetStatus(type: CaseType): AssetStatus {
  return type === 'OFFBOARDING' ? 'RETURNED' : 'DELIVERED'
}

/**
 * % d'avancement d'un dossier = part des objets ACTIFS ayant atteint le statut
 * cible. Les objets `REPLACED` (anciens, inactifs) sont exclus du dénominateur.
 */
export function computeProgress(c: OnboardingCase): number {
  const active = c.assets.filter((a) => a.status !== 'REPLACED')
  if (active.length === 0) return 0
  const target = targetStatus(c.type)
  const done = active.filter((a) => a.status === target).length
  return Math.round((done / active.length) * 100)
}

/** Nombre d'objets actifs terminés / total actif (pour un libellé « 3/9 »). */
export function progressCounts(c: OnboardingCase): { done: number; total: number } {
  const active = c.assets.filter((a) => a.status !== 'REPLACED')
  const target = targetStatus(c.type)
  return { done: active.filter((a) => a.status === target).length, total: active.length }
}

/* -------------------------------------------------------------------------- */
/* Remplacement de matériel (cycle M7)                                        */
/* -------------------------------------------------------------------------- */

/** Motifs de remplacement proposés (sélecteur). */
export const REPLACEMENT_REASONS: string[] = [
  'Panne',
  'Casse',
  'Vol',
  'Perte',
  'Montée en gamme',
  'Fin de vie',
  'Autre',
]

/**
 * Métadonnées d'affichage d'un statut de remplacement : libellé FR, couleur,
 * icône Heroicon outline (résolue côté composant). Icône + couleur + label.
 */
export const REPLACEMENT_STATUS_META: Record<
  ReplacementStatus,
  { label: string; badgeClass: string; icon: string }
> = {
  REQUESTED: { label: 'Demandé', badgeClass: 'badge-ghost', icon: 'ClipboardDocumentList' },
  ORDERED: { label: 'Commande en cours', badgeClass: 'badge-info', icon: 'ShoppingCart' },
  RECEIVED: { label: 'Nouveau reçu', badgeClass: 'badge-info', icon: 'InboxArrowDown' },
  OLD_COLLECTED: { label: 'Ancien récupéré', badgeClass: 'badge-warning', icon: 'ArrowUturnLeft' },
  NEW_DELIVERED: { label: 'Nouveau remis', badgeClass: 'badge-success', icon: 'CheckCircle' },
  DONE: { label: 'Terminé', badgeClass: 'badge-success', icon: 'CheckBadge' },
  CANCELLED: { label: 'Annulé', badgeClass: 'badge-ghost', icon: 'XCircle' },
}

/** Étapes ordonnées du workflow (hors annulation). */
export const REPLACEMENT_FLOW: ReplacementStatus[] = [
  'REQUESTED',
  'ORDERED',
  'RECEIVED',
  'OLD_COLLECTED',
  'NEW_DELIVERED',
  'DONE',
]

/** Statut suivant dans le flux (null si terminal / annulé / inconnu). */
export function nextReplacementStatus(status: ReplacementStatus): ReplacementStatus | null {
  const i = REPLACEMENT_FLOW.indexOf(status)
  return i >= 0 && i < REPLACEMENT_FLOW.length - 1 ? REPLACEMENT_FLOW[i + 1] : null
}

/** Libellé du bouton pour passer à l'étape suivante depuis un statut donné. */
export const REPLACEMENT_NEXT_ACTION: Record<ReplacementStatus, string | null> = {
  REQUESTED: 'Confirmer la commande',
  ORDERED: 'Marquer comme reçu',
  RECEIVED: "Récupérer l'ancien",
  OLD_COLLECTED: 'Remettre le nouveau',
  NEW_DELIVERED: 'Terminer',
  DONE: null,
  CANCELLED: null,
}

/** Un ticket est-il actif (en cours, ni terminé ni annulé) ? */
export function isReplacementActive(status: ReplacementStatus): boolean {
  return status !== 'DONE' && status !== 'CANCELLED'
}

/** % d'avancement d'un remplacement, dérivé de sa position dans le flux. */
export function replacementProgress(status: ReplacementStatus): number {
  if (status === 'CANCELLED') return 0
  const i = REPLACEMENT_FLOW.indexOf(status)
  return i < 0 ? 0 : Math.round((i / (REPLACEMENT_FLOW.length - 1)) * 100)
}

/** Métadonnées d'affichage de la source d'une demande de remplacement. */
export const REPLACEMENT_SOURCE_META: Record<ReplacementSource, { label: string }> = {
  EMAIL: { label: 'Email' },
  TEAMS: { label: 'Teams' },
  TICKET: { label: 'Ticket' },
  OTHER: { label: 'Autre' },
}

/** Motifs communs à tous les équipements. */
const COMMON_REPLACEMENT_REASONS = ['Perte', 'Vol', 'Autre']

/** Motifs de remplacement proposés selon la famille d'équipement. */
export const REPLACEMENT_REASONS_BY_CATEGORY: Record<AssetCategory, string[]> = {
  IT: ['En panne', 'Trop lent / obsolète', 'Écran cassé', 'Casse / chute', ...COMMON_REPLACEMENT_REASONS],
  TELECOM: ['Écran cassé', 'Batterie défaillante', 'En panne', 'Casse / chute', ...COMMON_REPLACEMENT_REASONS],
  VEHICLE: ['Accident', 'Panne mécanique', 'Vétusté / entretien', ...COMMON_REPLACEMENT_REASONS],
  ACCESS: ['Défectueux', 'Désactivé', ...COMMON_REPLACEMENT_REASONS],
  OTHER: ['Casse', 'Usure', ...COMMON_REPLACEMENT_REASONS],
}

/** Liste de motifs adaptée à la famille d'un objet (fallback : « Autre »). */
export function reasonsForAsset(category: AssetCategory): string[] {
  return REPLACEMENT_REASONS_BY_CATEGORY[category] ?? ['Autre']
}

/* -------------------------------------------------------------------------- */
/* État à la restitution (offboarding)                                        */
/* -------------------------------------------------------------------------- */

/** Échelle d'état à la fourniture/remise (inclut « Reconditionné »). */
export const ASSET_CONDITIONS: { value: string; label: string }[] = [
  { value: 'NEW', label: 'Neuf' },
  { value: 'REFURBISHED', label: 'Reconditionné' },
  { value: 'LIKE_NEW', label: 'Comme neuf' },
  { value: 'VERY_GOOD', label: 'Très bon' },
  { value: 'GOOD', label: 'Bon' },
  { value: 'FAIR', label: 'Satisfaisant' },
  { value: 'POOR', label: 'Mauvais' },
]

/** Échelle d'état d'un matériel rendu (du meilleur au pire, sans « Reconditionné »). */
export const RETURN_CONDITIONS: { value: string; label: string }[] = [
  { value: 'NEW', label: 'Neuf' },
  { value: 'LIKE_NEW', label: 'Comme neuf' },
  { value: 'VERY_GOOD', label: 'Très bon' },
  { value: 'GOOD', label: 'Bon' },
  { value: 'FAIR', label: 'Satisfaisant' },
  { value: 'POOR', label: 'Mauvais' },
]

/** Libellé FR d'un état (entrée ou sortie) — couvre tous les codes. */
export const CONDITION_LABEL: Record<string, string> = Object.fromEntries(
  ASSET_CONDITIONS.map((c) => [c.value, c.label]),
)

/** Alias rétro-compatible (état de restitution). */
export const RETURN_CONDITION_LABEL: Record<string, string> = CONDITION_LABEL

/** Types immatériels (lignes, accès) : pas d'état physique à la restitution. */
const NON_RETURNABLE_TYPES = new Set(['MOBILE_LINE', 'FIXED_LINE', 'TOOL_ACCESS'])

/** Un objet de ce type exige-t-il un état physique à la restitution ? */
export function requiresReturnCondition(typeCode: string): boolean {
  return !NON_RETURNABLE_TYPES.has(typeCode)
}
